/**
 * StatsDisplay component
 *
 * Shows voting statistics for an image
 */

import { useTranslation } from 'react-i18next';
import { ThumbsUp, Users, Target, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import type { ImageStats, AiStatus } from '@doyoulikethis/shared';
import { YES_REASONS, NO_REASONS } from '@doyoulikethis/shared';

interface StatsDisplayProps {
  stats: ImageStats;
  aiStatus: AiStatus;
}

export function StatsDisplay({ stats, aiStatus }: StatsDisplayProps) {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.language === 'ja';

  // Get reason label
  const getReasonLabel = (code: string): string => {
    const allReasons = { ...YES_REASONS, ...NO_REASONS };
    const reason = allReasons[code as keyof typeof allReasons];
    if (reason) {
      return isJapanese ? reason.ja : reason.en;
    }
    return code;
  };

  // Format time
  const formatTime = (ms: number | null): string => {
    if (ms === null) return '-';
    return (ms / 1000).toFixed(1);
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Like rate */}
      <Card variant="bordered" padding="sm">
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-200">
            <ThumbsUp className="h-4 w-4" aria-hidden="true" />
            <span>Like Rate</span>
          </div>
          <p className="mt-1 text-2xl font-bold">
            {stats.like_rate.toFixed(0)}%
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-200">
            {t('reveal.stats.votes_count', { count: stats.total_votes })}
          </p>
        </CardContent>
      </Card>

      {/* Top reason */}
      <Card variant="bordered" padding="sm">
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-200">
            <Users className="h-4 w-4" aria-hidden="true" />
            <span>Top Reason</span>
          </div>
          {stats.top_reasons.length > 0 && stats.top_reasons[0] ? (
            <>
              <p className="mt-1 text-sm font-medium">
                "{getReasonLabel(stats.top_reasons[0].reason_code)}"
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-200">
                {stats.top_reasons[0].percentage.toFixed(0)}% of voters
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-200">
              No reasons recorded yet
            </p>
          )}
        </CardContent>
      </Card>

      {/* AI perception accuracy */}
      <Card variant="bordered" padding="sm">
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-200">
            <Target className="h-4 w-4" aria-hidden="true" />
            <span>Correct Identifications</span>
          </div>
          {stats.ai_perception_accuracy !== null ? (
            <>
              <p className="mt-1 text-2xl font-bold">
                {stats.ai_perception_accuracy.toFixed(0)}%
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-200">
                correctly identified as {t(`reveal.ai_status.${aiStatus}`)}
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-200">
              Not enough data
            </p>
          )}
        </CardContent>
      </Card>

      {/* Average decision time */}
      <Card variant="bordered" padding="sm">
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-200">
            <Clock className="h-4 w-4" aria-hidden="true" />
            <span>Avg. Decision Time</span>
          </div>
          <p className="mt-1 text-2xl font-bold">
            {formatTime(stats.avg_time_to_vote_ms)}s
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-200">
            average time to vote
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
